import { session } from './session';
import { voiceTrace, voiceError } from './voiceDiagnostics';
export { session } from './session';
export const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export async function request(path, { method = 'GET', body, signal, token = session.get(), notifyUnauthorized = true, onResponse } = {}) {
  if (!API_ORIGIN) throw new Error('The existing Tap Talk Save backend is not configured.');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body && !(body instanceof FormData)) headers['Content-Type'] = 'application/json';
  let response;
  try {
    response = await fetch(`${API_ORIGIN}/api${path}`, {
      method, headers, signal,
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new Error(navigator.onLine ? 'Could not reach Tap Talk Save. Please try again.' : 'You’re offline. Reconnect to save your changes.');
  }
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  onResponse?.({ status: response.status, responseType: response.headers.get('content-type') || '' });
  if (!response.ok) {
    if (response.status === 401 && notifyUnauthorized && token && session.get() === token) {
      // Retain the credential: silently replacing a guest on an error could
      // strand their saves. Never open a sign-in modal from an API failure.
      window.dispatchEvent(new Event('tts-session-unavailable'));
    }
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    const message = response.status === 404 && path === '/auth/guest' ? 'Guest access is not available on the Tap Talk Save server yet. Please try again shortly.' :
      response.status === 401 ? 'Could not restore your saved session. Your device identifier has been kept. Please retry.' :
      response.status === 403 ? 'Your account does not currently have access to this action. Check your account status.' :
      detail && !detail.includes('_') ? detail : `That couldn’t be completed (${response.status}). Please try again.`;
    throw Object.assign(new Error(message), { status: response.status });
  }
  return data;
}

export const api = {
  guest: () => request('/auth/guest', { method: 'POST', body: {}, notifyUnauthorized: false }),
  me: () => request('/auth/me', { notifyUnauthorized: false }),
  exchange: (session_id, migration = null) => request('/auth/session', {
    method: 'POST', notifyUnauthorized: false,
    ...(migration ? { token: migration.token } : {}),
    body: { session_id, ...(migration ? { [migration.field]: migration.token } : {}) },
  }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  notes: () => request('/notes'),
  create: (text, input_method, options = {}) => request('/notes', { ...options, method: 'POST', body: { text, input_method, client_now: options.clientNow || localDateTime() } }),
  update: (id, body, options = {}) => request(`/notes/${encodeURIComponent(id)}`, { ...options, method: 'PATCH', body }),
  remove: (id, options = {}) => request(`/notes/${encodeURIComponent(id)}`, { ...options, method: 'DELETE' }),
  restore: id => request(`/notes/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  clearCompleted: () => request('/notes/clear-completed', { method: 'POST' }),
  settings: () => request('/settings'),
  updateSettings: body => request('/settings', { method: 'PUT', body: { ...body, tz_offset_minutes: -new Date().getTimezoneOffset() } }),
  transcribe: async (blob, extension, { signal, captureId, attempt = 'native' } = {}) => {
    if (!blob?.size) throw voiceError('NO_AUDIO_DATA', 'No sound was recorded. Please try the microphone again.');
    const controller = new AbortController(); let timedOut = false;
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 90000);
    const body = new FormData(); body.append('audio', blob, `recording.${extension}`);
    const started = Date.now();
    voiceTrace('transcription-request', { captureId, attempt, blobBytes: blob.size, mimeType: blob.type });
    try {
      const result = await request('/transcribe', { method: 'POST', body, signal: controller.signal, onResponse: metadata => voiceTrace('transcription-response', { captureId, attempt, ...metadata, durationMs: Date.now() - started }) });
      if (result?.error || result?.success === false) throw voiceError('TRANSCRIPTION_SERVICE_ERROR', 'Voice transcription is temporarily unavailable. Please try again shortly.');
      if (typeof result?.text !== 'string') throw voiceError('TRANSCRIPTION_INVALID_RESPONSE', 'I couldn’t transcribe this recording. Please try again.');
      if (!result.text.trim()) throw voiceError('TRANSCRIPTION_EMPTY_TEXT', 'I didn’t catch that. Please try again.');
      return result.text.trim();
    } catch (error) {
      const failure = timedOut ? voiceError('TRANSCRIPTION_TIMEOUT', 'Transcription took too long. Please try again.') : error;
      if (!failure.code) failure.code = failure.status ? `TRANSCRIPTION_HTTP_${failure.status}` : failure.name === 'AbortError' ? 'TRANSCRIPTION_CANCELLED' : 'TRANSCRIPTION_NETWORK_ERROR';
      // Keep internal status/code for recovery, but do not expose server or
      // codec details in the normal recording error message.
      if (failure.status) {
        if ([400, 415, 422].includes(failure.status)) failure.message = 'I couldn’t read this recording. Please try again.';
        else if ([401, 403].includes(failure.status)) failure.message = 'Your session could not be verified. Please refresh and try again.';
        else if (failure.status === 413) failure.message = 'That recording is too large. Please try a shorter message.';
        else if (failure.status === 429) failure.message = 'Voice transcription is busy. Please try again shortly.';
        else if ([408, 504].includes(failure.status)) failure.message = 'Transcription took too long. Please try again.';
        else failure.message = 'Voice transcription is temporarily unavailable. Please try again shortly.';
      }
      voiceTrace('transcription-error', { captureId, attempt, errorCode: failure.code, errorName: failure.name, status: failure.status || 0, durationMs: Date.now() - started });
      throw failure;
    } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
  },
};

// The mobile client sends local wall-clock time (not UTC) for natural-language dates.
export function localDateTime(date = new Date()) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
}